<script lang="ts" setup>
import { computed, ref } from 'vue';
import { BannerDecor } from '~/components';
import { AppTypography } from '~/components/base';
import type { Party } from '~/types/Candidates';
import { partyInitials } from '~/utils/partyInitials';
import { $pluralizeWithCount } from '~/utils/pluralize';

const { party } = defineProps<{
  party: Party;
  campaignsCount: number;
  lobbyistsCount: number;
}>();

const classificationLabel = computed(() =>
  party.classification === 'major' ? 'Major Party' : 'Minor Party',
);

// A 404 on the logo would otherwise show the browser's broken-image glyph
// rather than falling back to the initials.
const logoFailed = ref(false);

const initials = computed(() => partyInitials(party));
</script>

<template>
  <div data-testid="party-profile-card" class="profile-card">
    <div class="profile-card__band">
      <BannerDecor />
    </div>

    <span
      class="profile-card__avatar flex items-center justify-center"
      data-testid="party-profile-avatar"
    >
      <img
        v-if="party.logo && !logoFailed"
        :src="party.logo"
        :alt="party.name"
        width="112"
        height="112"
        decoding="async"
        class="profile-card__avatar-image size-full object-cover"
        @error="logoFailed = true"
      />
      <AppTypography
        v-else
        type="title-4"
        element="span"
        data-testid="party-profile-initials"
        >{{ initials }}</AppTypography
      >
    </span>

    <div class="profile-card__body flex flex-col items-center text-center">
      <span
        class="profile-card__chip inline-flex w-fit items-center"
        data-testid="party-profile-classification"
      >
        <AppTypography type="caption-bold" element="span" class="uppercase">{{
          classificationLabel
        }}</AppTypography>
      </span>

      <AppTypography type="title-5" element="p" class="mt-3">
        {{ party.name }}
      </AppTypography>

      <div class="profile-card__stats mt-3 flex items-center gap-3">
        <span class="flex items-center gap-1.5">
          <Icon name="solar:notes-linear" size="24" class="shrink-0" />
          <AppTypography type="body">
            {{ $pluralizeWithCount(campaignsCount, 'Campaign') }}
          </AppTypography>
        </span>
        <span class="profile-card__divider shrink-0" />
        <span class="flex items-center gap-1.5">
          <Icon name="solar:buildings-linear" size="24" class="shrink-0" />
          <AppTypography type="body">
            {{ $pluralizeWithCount(lobbyistsCount, 'Lobbyist') }}
          </AppTypography>
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.profile-card {
  position: relative;
  overflow: hidden;
  width: 100%;
  border: 1px solid $border-color;
  border-radius: $radius-lg;
  background-color: $surface-color-bg;

  @media (min-width: 1024px) {
    width: 360px;
    max-width: 360px;
    // Desktop lays this out as a fixed sidebar beside the tab panel. Without
    // this the card is shrinkable (width: 100% + overflow: hidden), so a
    // sibling panel whose content refuses to narrow can squeeze it to nothing.
    flex-shrink: 0;
  }

  &__band {
    position: relative;
    height: 85px;
    background-color: $surface-color-brand;
    overflow: hidden;
  }

  &__body {
    // The avatar overlaps the band by half its height, plus the
    // classification chip that hangs off the avatar's bottom edge; reserve
    // space here so the name/etc. clear both (chip bottom is 151px; band
    // 85px + this padding = 157px, with the name's own mt-3 covering the
    // rest) — same reasoning as candidates/ProfileCard.vue's chip clearance.
    padding: 72px $spacing-xl $spacing-xl;
  }

  &__avatar {
    position: absolute;
    top: 29px; // band height (85px) minus half the avatar height (56px)
    left: 50%;
    z-index: 1;
    // Clips the logo to the circular avatar; without it the image corners
    // would sit outside the ring.
    overflow: hidden;
    width: 112px;
    height: 112px;
    border: 4px solid $complementary-color;
    border-radius: 9999px;
    background-color: $surface-color-bg;
    color: $text-color-primary;
    transform: translateX(-50%);
  }

  &__avatar-image {
    border-radius: 9999px;
  }

  // Figma: the classification chip hangs off the bottom of the avatar —
  // vertically centred on the avatar's bottom edge (29px top + 112px tall =
  // 141px) and layered above it, matching candidates/ProfileCard.vue's chip.
  &__chip {
    position: absolute;
    top: 131px; // 141px avatar bottom, minus half the 20px chip height
    left: 50%;
    z-index: 2; // above the avatar (z-index: 1)
    padding: 2px $spacing-sm;
    border-radius: $radius-2xl;
    background-color: $complementary-color-shade-1;
    color: $text-color-brand;
    transform: translateX(-50%);
  }

  &__stats {
    color: $text-color-secondary;
  }

  &__divider {
    width: 1px;
    height: 16px;
    background-color: $border-color;
  }
}
</style>
